import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGalleryReload } from '../../context/GalleryReloadContext';
import SearchBar from '../SearchBar';

// Componente SearchBar específico para móvil que cierra el menú al buscar
const MobileSearchBar: React.FC<{ onSearch: () => void }> = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Extraer query de la URL al montar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q');
    if (query) {
      setSearchTerm(query);
    } else {
      setSearchTerm('');
    }
  }, [location.search, location.pathname]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      onSearch(); // Cerrar el menú móvil
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    // NO cerrar el menú móvil al limpiar, solo limpiar texto
  };

  return (
    <MobileSearchContainer>
      <form onSubmit={handleSubmit}>
        <MobileSearchInput
          type="text"
          placeholder="Buscar imágenes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Buscar imágenes"
        />
        {searchTerm ? (
          <MobileSearchButton 
            type="button" 
            onClick={handleClearSearch} 
            aria-label="Cancelar búsqueda"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor"/>
            </svg>
          </MobileSearchButton>
        ) : (
          <MobileSearchButton type="submit" aria-label="Buscar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor"/>
            </svg>
          </MobileSearchButton>
        )}
      </form>
    </MobileSearchContainer>
  );
};

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.medium} ${props => props.theme.spacing.xlarge};
  background: linear-gradient(90deg, #1c1c2e 0%, #252538 50%, #1c1c2e 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid ${props => props.theme.colors.border.primary};
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.medium};
  }
`;

const LogoIconLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  line-height: 0;
`;

const LogoText = styled(Link)`
  font-family: Arial, sans-serif;
  font-size: 1.8rem;
  font-weight: 100;
  text-decoration: none;
  color: #6e9eff;
  margin-left: ${props => props.theme.spacing.small};
`;

const DesktopSearchWrapper = styled.div`
  flex: 1;
  max-width: 450px;
  margin: 0 ${props => props.theme.spacing.xlarge};
  
  @media (max-width: 768px) {
    display: none !important;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.large};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: ${props => props.theme.spacing.small};
  transition: color ${props => props.theme.transitions.fast};
  
  &:hover {
    color: ${props => props.theme.colors.text.accent};
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    z-index: 1000;
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transition: all 0.3s ease-in-out;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 280px;
    background: linear-gradient(180deg, #1c1c2e 0%, #252538 100%);
    transform: translateX(${props => props.$isOpen ? '0' : '100%'});
    transition: transform 0.3s ease-in-out;
    z-index: 1001;
    padding: ${props => props.theme.spacing.xlarge} ${props => props.theme.spacing.large};
    box-shadow: -4px 0 12px rgba(0, 0, 0, 0.3);
    border-left: 1px solid ${props => props.theme.colors.border.primary};
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xlarge};
  padding-bottom: ${props => props.theme.spacing.large};
  border-bottom: 1px solid ${props => props.theme.colors.border.primary};
`;

const MobileMenuTitle = styled.h2`
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
`;

const MobileCloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xsmall};
  transition: color ${props => props.theme.transitions.fast};
  
  &:hover {
    color: ${props => props.theme.colors.text.accent};
  }
`;

const MobileMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.large};
`;

const MobileSearchWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing.large};
  padding-bottom: ${props => props.theme.spacing.large};
  border-bottom: 1px solid ${props => props.theme.colors.border.primary};
`;

const MobileSearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MobileSearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.colors.text.primary};
  transition: all ${props => props.theme.transitions.medium};
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.colors.text.secondary};
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.text.accent};
    background-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 2px rgba(74, 107, 175, 0.3);
  }
`;

const MobileSearchButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xsmall};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform ${props => props.theme.transitions.fast};
  
  &:hover {
    transform: translateY(-50%) scale(1.1);
  }
  
  & svg path {
    stroke: ${props => props.theme.colors.text.secondary};
    transition: stroke ${props => props.theme.transitions.fast};
  }
  
  &:hover svg path {
    stroke: ${props => props.theme.colors.text.accent};
  }
`;

const MobileNavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.medium};
`;

const MobileNavLink = styled(Link)`
  color: ${props => props.theme.colors.text.primary};
  text-decoration: none;
  font-weight: 500;
  padding: ${props => props.theme.spacing.medium};
  border-radius: ${props => props.theme.borderRadius.small};
  transition: all ${props => props.theme.transitions.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.small};
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${props => props.theme.colors.text.accent};
  }
`;

const MobileNavLinkDisabled = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  text-decoration: none;
  font-weight: 500;
  padding: ${props => props.theme.spacing.medium};
  border-radius: ${props => props.theme.borderRadius.small};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.small};
  opacity: 0.5;
  cursor: not-allowed;
  position: relative;
  
  &::after {
    content: '👤';
    margin-left: auto;
    font-size: 0.75rem;
    width: 18px;
    height: 18px;
    border: 1px solid ${props => props.theme.colors.text.secondary};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
  }
`;

const MobileProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.medium};
  padding-top: ${props => props.theme.spacing.large};
  border-top: 1px solid ${props => props.theme.colors.border.primary};
`;

const MobileProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.small};
  padding: ${props => props.theme.spacing.medium};
  background: rgba(255, 255, 255, 0.03);
  border-radius: ${props => props.theme.borderRadius.small};
  
  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid ${props => props.theme.colors.border.secondary};
  }
  
  span {
    color: ${props => props.theme.colors.text.primary};
    font-weight: 500;
  }
`;

const MobileButton = styled.button<{ variant?: 'primary' }>`
  background-color: ${props => props.variant === 'primary' ? props.theme.colors.button.primary.background : 'transparent'};
  color: ${props => props.variant === 'primary' ? props.theme.colors.button.primary.text : props.theme.colors.text.primary};
  border: ${props => props.variant === 'primary' ? 'none' : `1px solid ${props.theme.colors.border.primary}`};
  border-radius: ${props => props.theme.borderRadius.small};
  padding: ${props => props.theme.spacing.medium};
  font-weight: 500;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.medium};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.small};

  &:hover {
    background-color: ${props => props.variant === 'primary' ? props.theme.colors.button.primary.hover : 'rgba(255, 255, 255, 0.05)'};
    border-color: ${props => props.variant === 'primary' ? 'none' : props.theme.colors.border.secondary};
    color: ${props => props.variant === 'primary' ? props.theme.colors.button.primary.text : props.theme.colors.text.accent};
  }
`;

const UserIcon = styled.span<{ variant?: 'primary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid ${props => props.variant === 'primary' ? props.theme.colors.button.primary.text : props.theme.colors.text.primary};
  border-radius: 50%;
  font-size: 0.75rem;
  
  &::before {
    content: '👤';
  }
`;

const NavLinkExternal = styled.a`
  color: ${props => props.theme.colors.text.primary};
  text-decoration: none;
  font-weight: 500;
  padding: ${props => props.theme.spacing.xsmall} ${props => props.theme.spacing.small};
  border-radius: ${props => props.theme.borderRadius.small};
  transition: all ${props => props.theme.transitions.medium};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 50%;
    background: ${props => props.theme.colors.text.accent};
    transform: translateX(-50%);
    transition: width ${props => props.theme.transitions.medium};
    border-radius: 2px;
  }

  &:hover {
    color: ${props => props.theme.colors.text.accent};
    &::after { width: 80%; }
  }
`;

const MobileNavLinkExternal = styled.a`
  color: ${props => props.theme.colors.text.primary};
  text-decoration: none;
  font-weight: 500;
  padding: ${props => props.theme.spacing.medium};
  border-radius: ${props => props.theme.borderRadius.small};
  transition: all ${props => props.theme.transitions.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.small};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${props => props.theme.colors.text.accent};
  }
`;

const HomeIconLink = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-right: 6px;
  color: #6e9eff;
  opacity: 0.7;
  transition: opacity ${props => props.theme.transitions.fast}, transform ${props => props.theme.transitions.fast};

  &:hover {
    opacity: 1;
    transform: scale(1.12);
  }
`;

const LogoArea = styled.div`
  display: flex;
  align-items: center;
`;

const NavLink = styled(Link)`
  color: ${props => props.theme.colors.text.primary};
  text-decoration: none;
  font-weight: 500;
  padding: ${props => props.theme.spacing.xsmall} ${props => props.theme.spacing.small};
  border-radius: ${props => props.theme.borderRadius.small};
  transition: all ${props => props.theme.transitions.medium};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 50%;
    background: ${props => props.theme.colors.text.accent};
    transform: translateX(-50%);
    transition: width ${props => props.theme.transitions.medium};
    border-radius: 2px;
  }

  &:hover {
    color: ${props => props.theme.colors.text.accent};
    
    &::after {
      width: 80%;
    }
  }
`;

const ProfileContainer = styled.div`
  position: relative;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.small};
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.text.primary};
  font-weight: 500;
  transition: color ${props => props.theme.transitions.fast};
  
  &:hover {
    color: ${props => props.theme.colors.text.accent};
  }
  
  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid ${props => props.theme.colors.border.secondary};
    transition: border-color ${props => props.theme.transitions.fast};
    
    &:hover {
      border-color: ${props => props.theme.colors.text.accent};
    }
  }
`;

const ProfileDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${props => props.theme.spacing.small};
  background: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  min-width: 160px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
  transition: all 0.2s ease-in-out;
`;

const ProfileDropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.small};
  padding: ${props => props.theme.spacing.medium};
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  font-weight: 500;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  text-align: left;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${props => props.theme.colors.text.accent};
  }
  
  &:first-child {
    border-radius: ${props => props.theme.borderRadius.medium} ${props => props.theme.borderRadius.medium} 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 ${props => props.theme.borderRadius.medium} ${props => props.theme.borderRadius.medium};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.small};
`;

const SignInButton = styled.button`
  background-color: transparent;
  color: ${props => props.theme.colors.text.primary};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.small};
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  font-weight: 500;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.medium};

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: ${props => props.theme.colors.border.secondary};
    color: ${props => props.theme.colors.text.accent};
  }
`;

const SignUpButton = styled.button`
  background-color: ${props => props.theme.colors.button.primary.background};
  color: ${props => props.theme.colors.button.primary.text};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  font-weight: 500;
  cursor: pointer;
  transition: background-color ${props => props.theme.transitions.medium};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  &:hover {
    background-color: ${props => props.theme.colors.button.primary.hover};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
    transform: translateY(-1px);
  }
`;

const Header: React.FC = () => {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerReload } = useGalleryReload();
  
  // Estado para forzar la limpieza de la búsqueda
  const [resetSearch, setResetSearch] = useState(false);
  // Estado para el menú móvil
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Estado para el dropdown del perfil
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const handleSignInClick = () => {
    navigate('/login', { state: { mode: 'signin' } });
  };

  const handleSignUpClick = () => {
    navigate('/login', { state: { mode: 'signup' } });
  };

  const handleLogoutClick = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
  };
  
  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };
  
  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (profileDropdownOpen && !target.closest('[data-profile-dropdown]')) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };
  
  const handleMobileNavigation = (path: string, onClick?: () => void) => {
    return (e: React.MouseEvent) => {
      e.preventDefault();
      setMobileMenuOpen(false);
      
      if (onClick) {
        onClick();
      } else {
        navigate(path);
      }
    };
  };
  
  // Función para navegar a la página principal y limpiar la búsqueda
  const handleHomeNavigation = (e: React.MouseEvent) => {
    e.preventDefault();
    const seed = Math.floor(Math.random() * 1000000) + 1;
    navigate(`/?seed=${seed}`, { replace: true });
    setResetSearch(!resetSearch);
  };
  
  // Función para navegar a Explorar (página principal sin búsqueda)
  const handleExploreClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault(); // Prevenir comportamiento por defecto del enlace
    
    // Si ya estamos en la página principal, solo forzar la recarga de imágenes
    if (location.pathname === '/') {
      triggerReload(); // Forzar recarga de la galería
    }
    
    // Siempre navegar a la página principal sin parámetros,
    // sin importar en qué página estemos actualmente
    navigate('/', { replace: true });
    setResetSearch(!resetSearch); // Cambiar para forzar la limpieza
  };

  return (
    <>
      <HeaderContainer>
        <LogoArea>
          <HomeIconLink href="https://canvasoft.com" title="Canvasoft Home">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
              <path d="M9 21V12h6v9"/>
            </svg>
          </HomeIconLink>
          <LogoIconLink to="/" aria-label="Canvasoft">
            <img src="/logo194.png" width="32" height="32" alt="Canvasoft logo" />
          </LogoIconLink>
          <LogoText to="/" onClick={handleHomeNavigation}>CANVASOFT</LogoText>
        </LogoArea>
        
        <DesktopSearchWrapper>
          <SearchBar key={`search-${resetSearch}`} />
        </DesktopSearchWrapper>
        
        <Nav>
          <NavLinkExternal href="https://canvasoft.com">Home</NavLinkExternal>
          <NavLink to="/" onClick={handleExploreClick}>Explorar</NavLink>
          {user && (
            <>
              <NavLink to="/favorites">Favoritos</NavLink>
              <NavLink to="/cart">Carrito</NavLink>
              <ProfileContainer data-profile-dropdown>
                <ProfileButton onClick={toggleProfileDropdown}>
                  {user.profilePicture && <img src={user.profilePicture} alt={user.name} />}
                  {user.name}
                </ProfileButton>
                <ProfileDropdown $isOpen={profileDropdownOpen}>
                  <ProfileDropdownItem onClick={handleLogoutClick}>
                    🚪 Cerrar Sesión
                  </ProfileDropdownItem>
                </ProfileDropdown>
              </ProfileContainer>
            </>
          )}
          {!user && status !== 'loading' && (
            <ButtonGroup>
              <SignInButton onClick={handleSignInClick}>
                Sign in
              </SignInButton>
              <SignUpButton onClick={handleSignUpClick}>
                Sign up
              </SignUpButton>
            </ButtonGroup>
          )}
        </Nav>
        
        <MobileMenuButton onClick={toggleMobileMenu}>
          ☰
        </MobileMenuButton>
      </HeaderContainer>
      
      <MobileMenuOverlay $isOpen={mobileMenuOpen} onClick={closeMobileMenu} />
      
      <MobileMenu $isOpen={mobileMenuOpen}>
        <MobileMenuHeader>
          <MobileMenuTitle>Menú</MobileMenuTitle>
          <MobileCloseButton onClick={closeMobileMenu}>×</MobileCloseButton>
        </MobileMenuHeader>
        
        <MobileMenuContent>
          <MobileSearchWrapper>
            <MobileSearchBar 
              key={`mobile-search-${resetSearch}`} 
              onSearch={closeMobileMenu}
            />
          </MobileSearchWrapper>
          
          <MobileNavSection>
            <MobileNavLinkExternal href="https://canvasoft.com">
              🏠 Home
            </MobileNavLinkExternal>
            <MobileNavLink
              to="/"
              onClick={handleMobileNavigation('/', () => {
                if (location.pathname === '/') {
                  triggerReload();
                }
                navigate('/', { replace: true });
                setResetSearch(!resetSearch);
              })}
            >
              🌐 Explorar
            </MobileNavLink>
            
            {user ? (
              <>
                <MobileNavLink 
                  to="/favorites" 
                  onClick={handleMobileNavigation('/favorites')}
                >
                  💜 Favoritos
                </MobileNavLink>
                <MobileNavLink 
                  to="/cart" 
                  onClick={handleMobileNavigation('/cart')}
                >
                  🛍️ Carrito
                </MobileNavLink>
              </>
            ) : (
              <>
                <MobileNavLinkDisabled>
                  💜 Favoritos
                </MobileNavLinkDisabled>
                <MobileNavLinkDisabled>
                  🛍️ Carrito
                </MobileNavLinkDisabled>
              </>
            )}
          </MobileNavSection>
          
          <MobileProfileSection>
            {user ? (
              <>
                <MobileProfileInfo>
                  {user.profilePicture && <img src={user.profilePicture} alt={user.name} />}
                  <span>{user.name}</span>
                </MobileProfileInfo>
                <MobileButton onClick={handleMobileNavigation('/', handleLogoutClick)}>
                  Cerrar Sesión
                </MobileButton>
              </>
            ) : status !== 'loading' && (
              <>
                <MobileButton 
                  onClick={handleMobileNavigation('/login', () => {
                    navigate('/login', { state: { mode: 'signin' } });
                  })}
                >
                  <UserIcon />
                  Sign in
                </MobileButton>
                <MobileButton 
                  variant="primary"
                  onClick={handleMobileNavigation('/login', () => {
                    navigate('/login', { state: { mode: 'signup' } });
                  })}
                >
                  Sign up
                </MobileButton>
              </>
            )}
          </MobileProfileSection>
        </MobileMenuContent>
      </MobileMenu>
    </>
  );
};

export default Header;